import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, BehaviorSubject, fromEvent, merge } from 'rxjs';
import { takeUntil, throttleTime, filter } from 'rxjs/operators';
import { SessionTimeoutModalComponent } from '../shared/components/session-timeout-modal/session-timeout-modal.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { PendingChangesService } from './pending-changes.service';

const TIMEOUT_MS = environment.idleTimeoutMs;
const WARNING_MS = environment.idleWarningMs;
const ACTIVITY_THROTTLE_MS = environment.activityThrottleMs;
const KEEP_ALIVE_INTERVAL_MS = environment.keepAliveIntervalMs;
const COUNTDOWN_INTERVAL_MS = environment.countdownIntervalMs;
const STORAGE_KEY = environment.idleStorageKey;
// Used to broadcast warning/expired events across tabs so all tabs show the countdown
// and redirect to login together when the session expires
const SESSION_EVENT_KEY = environment.sessionEventKey;

interface SessionPingResponse {
	authenticated: boolean;
	session_lifetime_minutes: number;
	expires_at: string;
	remaining_minutes: number;
	server_time: string;
}

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
	private destroy$ = new Subject<void>();
	private warningTimer: ReturnType<typeof setTimeout> | null = null;
	private countdownInterval: ReturnType<typeof setInterval> | null = null;
	private keepAliveInterval: ReturnType<typeof setInterval> | null = null;
	private lastActivityTs = 0;
	private isRunning = false;
	private showingWarning = false;
	private dialogRef: DynamicDialogRef | null = null;
	private storageHandler = (e: StorageEvent) => this.onStorageChange(e);
	private visibilityHandler = () => this.onVisibilityChange();

	// Observable for countdown - modal component will subscribe to this
	countdown$ = new BehaviorSubject<number>(0);

	constructor(
		private router: Router,
		private dialogService: DialogService,
		private http: HttpClient,
		private ngZone: NgZone,
		private pendingChanges: PendingChangesService
	) {}

	start(): void {
		if (this.isRunning) return;
		this.isRunning = true;

		this.setupActivityListeners();
		this.setupStorageListener();
		this.setupVisibilityListener();
		this.recordActivity();
		this.startKeepAlive();
	}

	stop(): void {
		this.isRunning = false;
		this.clearAllTimers();
		this.removeStorageListener();
		this.removeVisibilityListener();
		this.closeModal();
	}

	ngOnDestroy(): void {
		this.stop();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private setupActivityListeners(): void {
		// Run outside Angular zone for performance (no change detection on every mouse move)
		this.ngZone.runOutsideAngular(() => {
			const activityEvents$ = merge(
				fromEvent(document, 'mousemove'),
				fromEvent(document, 'keydown'),
				fromEvent(document, 'scroll'),
				fromEvent(document, 'touchstart'),
				fromEvent(document, 'click')
			);

			activityEvents$
				.pipe(
					takeUntil(this.destroy$),
					throttleTime(ACTIVITY_THROTTLE_MS),
					filter(() => this.isRunning && !this.showingWarning)
				)
				.subscribe(() => {
					this.ngZone.run(() => this.recordActivity());
				});
		});
	}

	private setupStorageListener(): void {
		window.addEventListener('storage', this.storageHandler);
	}

	private removeStorageListener(): void {
		window.removeEventListener('storage', this.storageHandler);
	}

	private setupVisibilityListener(): void {
		document.addEventListener('visibilitychange', this.visibilityHandler);
	}

	private removeVisibilityListener(): void {
		document.removeEventListener('visibilitychange', this.visibilityHandler);
	}

	/**
	 * Fired when a tab transitions from hidden to visible (e.g. user switches back to this tab).
	 * Background tabs can have their timers throttled or paused by the browser, so they may
	 * have "missed" the inactivity period without their timers firing. This handler catches up
	 * by comparing wall-clock time against the last recorded activity timestamp in localStorage.
	 */
	private onVisibilityChange(): void {
		if (document.visibilityState !== 'visible' || !this.isRunning) return;

		// Read last activity from localStorage so we also catch activity that happened
		// in other tabs while this one was in the background
		const lastActivity = Number(localStorage.getItem(STORAGE_KEY)) || this.lastActivityTs;
		const elapsed = Date.now() - lastActivity;

		if (elapsed >= TIMEOUT_MS) {
			// The full timeout has passed — this tab should have been logged out already
			this.ngZone.run(() => this.logout('Session expired due to inactivity.'));
		} else if (elapsed >= WARNING_MS) {
			// The warning threshold has passed — show the countdown with the real remaining time
			const remainingMs = TIMEOUT_MS - elapsed;
			this.ngZone.run(() => {
				if (this.showingWarning) {
					// Modal is already open (timer fired before tab went to background) —
					// correct the countdown to reflect real elapsed time
					const remainingSeconds = Math.ceil(remainingMs / 1000);
					this.countdown$.next(remainingSeconds);
				} else {
					// Modal not open yet (timer was throttled while in background) —
					// clear the stale timer and show the modal immediately
					this.clearWarningTimer();
					this.showWarningModal(remainingMs);
				}
			});
		} else {
			// Still within the idle period — reset the warning timer using the real remaining
			// delay so it fires at the correct wall-clock time
			const remainingDelay = WARNING_MS - elapsed;
			this.ngZone.run(() => this.resetWarningTimer(remainingDelay));
		}
	}

	/** storage listener event */
	private onStorageChange(event: StorageEvent): void {
		if (!this.isRunning) return;

		if (event.key === STORAGE_KEY) {
			const newTimestamp = Number(event.newValue);
			if (newTimestamp > this.lastActivityTs) {
				// Activity detected in another tab - reset our timer
				this.lastActivityTs = newTimestamp;
				this.resetWarningTimer();
				if (this.showingWarning) {
					this.closeModal();
				}
			}
			return;
		}

		// Cross-tab session event: another tab broadcasted a warning or expiry.
		// This ensures all tabs show the countdown and log out together.
		if (event.key === SESSION_EVENT_KEY && event.newValue) {
			const sessionEvent = JSON.parse(event.newValue);

			if (sessionEvent.type === 'warning' && !this.showingWarning) {
				// Another tab triggered the warning — show modal with the same shared deadline
				// so all tabs count down in sync
				const remainingMs = sessionEvent.deadlineTs - Date.now();
				this.ngZone.run(() => {
					if (remainingMs > 0) {
						this.clearWarningTimer();
						this.showWarningModal(remainingMs);
					} else {
						// Deadline already passed by the time this tab received the event
						this.logout('Session expired due to inactivity.');
					}
				});
			} else if (sessionEvent.type === 'expired') {
				// Another tab's countdown hit zero — log out this tab too
				this.ngZone.run(() => this.logout('Session expired due to inactivity.'));
			}
		}
	}

	private recordActivity(): void {
		this.lastActivityTs = Date.now();
		localStorage.setItem(STORAGE_KEY, String(this.lastActivityTs));
		this.resetWarningTimer();
	}

	private clearWarningTimer(): void {
		if (this.warningTimer) {
			clearTimeout(this.warningTimer);
			this.warningTimer = null;
		}
	}

	private resetWarningTimer(delay = WARNING_MS): void {
		this.clearWarningTimer();
		this.warningTimer = setTimeout(() => this.showWarningModal(), delay);
	}

	private startKeepAlive(): void {
		this.keepAliveInterval = setInterval(() => {
			if (this.isRunning && !this.showingWarning) {
				this.pingSession();
			}
		}, KEEP_ALIVE_INTERVAL_MS);
	}

	private pingSession(): void {
		this.http
			.get<SessionPingResponse>(`${environment.apiUrl}session-ping`, { withCredentials: true })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					// Session is still valid
				},
				error: (err) => {
					if (err.status === 401) {
						this.logout('Session expired. Please log in again.');
					} else if (err.status === 403 && err.error?.requiresPasswordReset) {
						this.stop();
						this.pendingChanges.discardAll();
						this.router.navigate(['/auth/force-reset-password']);
					}
				}
			});
	}

	private showWarningModal(remainingMs = TIMEOUT_MS - WARNING_MS): void {
		if (this.showingWarning) return;
		this.showingWarning = true;

		const deadlineTs = Date.now() + remainingMs;

		// Broadcast the shared deadline to other tabs so they open their own warning modal
		// with the same countdown, keeping all tabs in sync
		localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ type: 'warning', deadlineTs }));

		let remainingSeconds = Math.ceil(remainingMs / 1000);
		this.countdown$.next(remainingSeconds);

		this.dialogRef = this.dialogService.open(SessionTimeoutModalComponent, {
			header: 'Session Timeout Warning',
			width: '400px',
			closable: false,
			closeOnEscape: false,
			modal: true,
			data: {
				countdown$: this.countdown$
			}
		});

		// Start countdown using wall-clock time to stay accurate even if throttled
		this.countdownInterval = setInterval(() => {
			remainingSeconds = Math.ceil((deadlineTs - Date.now()) / 1000);
			this.countdown$.next(remainingSeconds);

			if (remainingSeconds <= 0) {
				this.logout('Session expired due to inactivity.');
			}
		}, COUNTDOWN_INTERVAL_MS);

		this.dialogRef.onClose.subscribe((result: 'extend' | 'logout' | undefined) => {
			this.clearCountdown();
			this.showingWarning = false;

			if (result === 'extend') {
				this.extendSession();
			} else if (result === 'logout') {
				this.logout();
			}
			// If undefined (should not happen since modal is not closable), treat as logout
			if (result === undefined) {
				this.logout('Session expired due to inactivity.');
			}
		});
	}

	private closeModal(): void {
		if (this.dialogRef) {
			this.dialogRef.close('extend');
			this.dialogRef = null;
		}
		this.showingWarning = false;
		this.clearCountdown();
	}

	private extendSession(): void {
		this.recordActivity();
		this.pingSession();
	}

	private logout(message?: string): void {
		// Broadcast expiry to other tabs before stop() removes our storage listener.
		// Other tabs will receive this event and call logout() themselves, ensuring
		// all tabs redirect to the login page together.
		localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ type: 'expired' }));

		this.stop();

		// Invalidate the server-side session so stale modals cannot submit data
		this.http
			.post(`${environment.apiUrl}auth/logout`, {}, { withCredentials: true })
			.subscribe({ error: () => {} }); // fire and forget

		localStorage.removeItem('roles');
		localStorage.removeItem('permissions');
		localStorage.removeItem('loggedInUser');
		localStorage.removeItem(STORAGE_KEY);
		localStorage.removeItem(SESSION_EVENT_KEY);

		const currentUrl = window.location.pathname + window.location.search;
		if (currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/auth')) {
			sessionStorage.setItem('redirect_after_login', currentUrl);
		}

		// Immediately remove all PrimeNG dialog overlays from the DOM before navigating.
		// We cannot use DialogService.close() here because DialogService is provided per-module,
		// the root-level instance has no knowledge of dialogs opened by feature modules.
		// Querying .p-dialog-mask directly targets all PrimeNG dialogs (DynamicDialog, p-dialog,
		// p-confirmDialog) regardless of which module or DialogService instance opened them.
		// This prevents any open modal from remaining interactive after session expiry.
		document.querySelectorAll('.p-dialog-mask').forEach((el) => el.remove());

		// We intentionally use window.location.href instead of Angular's router.navigate() here.
		// router.navigate() keeps the Angular app alive, so any open modals would survive the
		// route change and remain interactive — a security risk after session expiry.
		// window.location.href triggers a full page reload, tearing down the entire Angular app
		// and ensuring no stale in-memory state persists.
		// DOVE-2676 — drop any unsent change so the reload doesn't show the browser's "Leave site?" prompt.
		this.pendingChanges.discardAll();
		const url = message ? `/auth/login?message=${encodeURIComponent(message)}` : '/auth/login';
		window.location.href = url;
	}

	private clearAllTimers(): void {
		this.clearWarningTimer();
		this.clearCountdown();
		if (this.keepAliveInterval) {
			clearInterval(this.keepAliveInterval);
			this.keepAliveInterval = null;
		}
	}

	private clearCountdown(): void {
		if (this.countdownInterval) {
			clearInterval(this.countdownInterval);
			this.countdownInterval = null;
		}
	}
}
